import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { BankAccount, CreditCard } from '@ops/shared-types';

// Actions for Bank Accounts
export class LoadBankAccounts {
  static readonly type = '[Payment Methods] Load Bank Accounts';
  constructor(public companyId: string) {}
}

export class LoadBankAccountsSuccess {
  static readonly type = '[Payment Methods] Load Bank Accounts Success';
  constructor(public bankAccounts: BankAccount[]) {}
}

export class LoadBankAccountsFailure {
  static readonly type = '[Payment Methods] Load Bank Accounts Failure';
  constructor(public error: string) {}
}

export class CreateBankAccount {
  static readonly type = '[Payment Methods] Create Bank Account';
  constructor(public bankAccount: Partial<BankAccount>, public companyId: string) {}
}

export class CreateBankAccountSuccess {
  static readonly type = '[Payment Methods] Create Bank Account Success';
  constructor(public bankAccount: BankAccount) {}
}

export class CreateBankAccountFailure {
  static readonly type = '[Payment Methods] Create Bank Account Failure';
  constructor(public error: string) {}
}

export class UpdateBankAccount {
  static readonly type = '[Payment Methods] Update Bank Account';
  constructor(public bankAccount: BankAccount, public companyId: string) {}
}

export class UpdateBankAccountSuccess {
  static readonly type = '[Payment Methods] Update Bank Account Success';
  constructor(public bankAccount: BankAccount) {}
}

export class UpdateBankAccountFailure {
  static readonly type = '[Payment Methods] Update Bank Account Failure';
  constructor(public error: string) {}
}

// Actions for Credit Cards
export class LoadCreditCards {
  static readonly type = '[Payment Methods] Load Credit Cards';
  constructor(public companyId: string) {}
}

export class LoadCreditCardsSuccess {
  static readonly type = '[Payment Methods] Load Credit Cards Success';
  constructor(public creditCards: CreditCard[]) {}
}

export class LoadCreditCardsFailure {
  static readonly type = '[Payment Methods] Load Credit Cards Failure';
  constructor(public error: string) {}
}

export class CreateCreditCard {
  static readonly type = '[Payment Methods] Create Credit Card';
  constructor(public creditCard: Partial<CreditCard>, public companyId: string) {}
}

export class CreateCreditCardSuccess {
  static readonly type = '[Payment Methods] Create Credit Card Success';
  constructor(public creditCard: CreditCard) {}
}

export class CreateCreditCardFailure {
  static readonly type = '[Payment Methods] Create Credit Card Failure';
  constructor(public error: string) {}
}

export class UpdateCreditCard {
  static readonly type = '[Payment Methods] Update Credit Card';
  constructor(public creditCard: CreditCard, public companyId: string) {}
}

export class UpdateCreditCardSuccess {
  static readonly type = '[Payment Methods] Update Credit Card Success';
  constructor(public creditCard: CreditCard) {}
}

export class UpdateCreditCardFailure {
  static readonly type = '[Payment Methods] Update Credit Card Failure';
  constructor(public error: string) {}
}

// Clear state action
export class ClearPaymentMethodsState {
  static readonly type = '[Payment Methods] Clear State';
}

// State Model
export interface PaymentMethodsStateModel {
  bankAccounts: BankAccount[];
  creditCards: CreditCard[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  currentCompanyId: string | null;
}

// Initial State
const initialState: PaymentMethodsStateModel = {
  bankAccounts: [],
  creditCards: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
  currentCompanyId: null,
};

@State<PaymentMethodsStateModel>({
  name: 'paymentMethods',
  defaults: initialState,
})
@Injectable()
export class PaymentMethodsState {
  // Selectors
  @Selector()
  static getBankAccounts(state: PaymentMethodsStateModel): BankAccount[] {
    return state.bankAccounts;
  }

  @Selector()
  static getCreditCards(state: PaymentMethodsStateModel): CreditCard[] {
    return state.creditCards;
  }

  @Selector()
  static getActiveBankAccounts(state: PaymentMethodsStateModel): BankAccount[] {
    return state.bankAccounts.filter(account => !account.isDebit);
  }

  @Selector()
  static getActiveCreditCards(state: PaymentMethodsStateModel): CreditCard[] {
    return state.creditCards.filter(card => card.isActive);
  }

  @Selector()
  static getFavoriteBankAccounts(state: PaymentMethodsStateModel): BankAccount[] {
    return state.bankAccounts.filter(account => account.isFavorite);
  }

  @Selector()
  static getFavoriteCreditCards(state: PaymentMethodsStateModel): CreditCard[] {
    return state.creditCards.filter(card => card.isFavorite);
  }

  @Selector()
  static getBankAccountsByCurrency(state: PaymentMethodsStateModel) {
    return (currencyCode: string) => 
      state.bankAccounts.filter(account => account.currencyCode === currencyCode);
  }

  @Selector()
  static getCreditCardsByBankAccount(state: PaymentMethodsStateModel) {
    return (bankAccountId: string) => 
      state.creditCards.filter(card => card.bankAccount === bankAccountId);
  }

  @Selector()
  static isLoading(state: PaymentMethodsStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static getError(state: PaymentMethodsStateModel): string | null {
    return state.error;
  }

  @Selector()
  static getLastUpdated(state: PaymentMethodsStateModel): Date | null {
    return state.lastUpdated;
  }

  @Selector()
  static getCurrentCompanyId(state: PaymentMethodsStateModel): string | null {
    return state.currentCompanyId;
  }

  @Selector()
  static hasData(state: PaymentMethodsStateModel): boolean {
    return state.bankAccounts.length > 0 || state.creditCards.length > 0;
  }

  // Bank Account Actions
  @Action(LoadBankAccounts)
  loadBankAccounts(ctx: StateContext<PaymentMethodsStateModel>, action: LoadBankAccounts) {
    ctx.patchState({
      isLoading: true,
      error: null,
      currentCompanyId: action.companyId,
    });
  }

  @Action(LoadBankAccountsSuccess)
  loadBankAccountsSuccess(ctx: StateContext<PaymentMethodsStateModel>, action: LoadBankAccountsSuccess) {
    ctx.patchState({
      bankAccounts: action.bankAccounts,
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }

  @Action(LoadBankAccountsFailure)
  loadBankAccountsFailure(ctx: StateContext<PaymentMethodsStateModel>, action: LoadBankAccountsFailure) {
    ctx.patchState({
      isLoading: false,
      error: action.error,
    });
  }

  @Action(CreateBankAccount)
  createBankAccount(ctx: StateContext<PaymentMethodsStateModel>, action: CreateBankAccount) {
    ctx.patchState({
      isLoading: true,
      error: null,
    });
  }

  @Action(CreateBankAccountSuccess)
  createBankAccountSuccess(ctx: StateContext<PaymentMethodsStateModel>, action: CreateBankAccountSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      bankAccounts: [...state.bankAccounts, action.bankAccount],
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }

  @Action(CreateBankAccountFailure)
  createBankAccountFailure(ctx: StateContext<PaymentMethodsStateModel>, action: CreateBankAccountFailure) {
    ctx.patchState({
      isLoading: false,
      error: action.error,
    });
  }

  @Action(UpdateBankAccount)
  updateBankAccount(ctx: StateContext<PaymentMethodsStateModel>, action: UpdateBankAccount) {
    ctx.patchState({
      isLoading: true,
      error: null,
    });
  }

  @Action(UpdateBankAccountSuccess)
  updateBankAccountSuccess(ctx: StateContext<PaymentMethodsStateModel>, action: UpdateBankAccountSuccess) {
    const state = ctx.getState();
    const updatedBankAccounts = state.bankAccounts.map(account =>
      account._id === action.bankAccount._id ? action.bankAccount : account
    );
    
    ctx.patchState({
      bankAccounts: updatedBankAccounts,
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }

  @Action(UpdateBankAccountFailure)
  updateBankAccountFailure(ctx: StateContext<PaymentMethodsStateModel>, action: UpdateBankAccountFailure) {
    ctx.patchState({
      isLoading: false,
      error: action.error,
    });
  }

  // Credit Card Actions
  @Action(LoadCreditCards)
  loadCreditCards(ctx: StateContext<PaymentMethodsStateModel>, action: LoadCreditCards) {
    ctx.patchState({
      isLoading: true,
      error: null,
      currentCompanyId: action.companyId,
    });
  }

  @Action(LoadCreditCardsSuccess)
  loadCreditCardsSuccess(ctx: StateContext<PaymentMethodsStateModel>, action: LoadCreditCardsSuccess) {
    ctx.patchState({
      creditCards: action.creditCards,
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }

  @Action(LoadCreditCardsFailure)
  loadCreditCardsFailure(ctx: StateContext<PaymentMethodsStateModel>, action: LoadCreditCardsFailure) {
    ctx.patchState({
      isLoading: false,
      error: action.error,
    });
  }

  @Action(CreateCreditCard)
  createCreditCard(ctx: StateContext<PaymentMethodsStateModel>, action: CreateCreditCard) {
    ctx.patchState({
      isLoading: true,
      error: null,
    });
  }

  @Action(CreateCreditCardSuccess)
  createCreditCardSuccess(ctx: StateContext<PaymentMethodsStateModel>, action: CreateCreditCardSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      creditCards: [...state.creditCards, action.creditCard],
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }

  @Action(CreateCreditCardFailure)
  createCreditCardFailure(ctx: StateContext<PaymentMethodsStateModel>, action: CreateCreditCardFailure) {
    ctx.patchState({
      isLoading: false,
      error: action.error,
    });
  }

  @Action(UpdateCreditCard)
  updateCreditCard(ctx: StateContext<PaymentMethodsStateModel>, action: UpdateCreditCard) {
    ctx.patchState({
      isLoading: true,
      error: null,
    });
  }

  @Action(UpdateCreditCardSuccess)
  updateCreditCardSuccess(ctx: StateContext<PaymentMethodsStateModel>, action: UpdateCreditCardSuccess) {
    const state = ctx.getState();
    const updatedCreditCards = state.creditCards.map(card =>
      card._id === action.creditCard._id ? action.creditCard : card
    );
    
    ctx.patchState({
      creditCards: updatedCreditCards,
      isLoading: false,
      error: null,
      lastUpdated: new Date(),
    });
  }

  @Action(UpdateCreditCardFailure)
  updateCreditCardFailure(ctx: StateContext<PaymentMethodsStateModel>, action: UpdateCreditCardFailure) {
    ctx.patchState({
      isLoading: false,
      error: action.error,
    });
  }

  @Action(ClearPaymentMethodsState)
  clearPaymentMethodsState(ctx: StateContext<PaymentMethodsStateModel>) {
    ctx.setState(initialState);
  }
}
